import { Popup } from "devextreme-react/popup";
import Button from "devextreme-react/button";
import React, { useCallback, useEffect, useState } from "react";
import { EmailTemplateContentDto } from "../../../dtos/EmailTemplateContentDto";
import { StatusTransitionConfig } from "../../../dtos/StatusTransitionConfig";
import { getEmailTemplate, customizeEmailTemplate, resetEmailTemplate, getEmailTemplateTokens, TemplateTokenInfo } from "../../../api/v2/TasklistsApi";
import type { ResetEmailTemplateDto } from "../../../dtos/ResetEmailTemplateDto";
import { StepStatus } from "../../../dtos/StepStatus";
import TokenEditor from "../../shared/TokenEditor";
import TemplateAttachments from "./components/TemplateAttachments";
import "./EmailTemplateViewerDialog.css";

type Props = {
    visible: boolean;
    onHiding: () => void;
    templateName: string | null;
    specialSettings?: StatusTransitionConfig | null;
    contextId?: number;
    contextType?: "task" | "subtask";
    phaseTypeId?: number;
    // Context passed to the backend on reset so it can look up the current default.
    resetContext?: Partial<ResetEmailTemplateDto>;
    onTemplateCustomized?: (newTemplateName: string) => void;
    onTemplateReset?: (coreTemplateName: string) => void;
    onSettingsChanged?: (updates: Partial<StatusTransitionConfig>) => void;
};

// Empty fallback when tokens haven't loaded yet
const emptyTokens: TemplateTokenInfo[] = [];

function getStepStatusLabel(status: StepStatus): string {
    switch (status) {
        case StepStatus.NOT_STARTED: return "Not Started";
        case StepStatus.SETUP: return "In Progress";
        case StepStatus.READY: return "Ready";
        case StepStatus.GRANTEE_PENDING: return "Grantee Pending";
        case StepStatus.GRANTEE_PENDING_REVISION: return "Grantee Pending Revision";
        case StepStatus.GRANTEE_COMPLETE: return "Grantee Complete";
        case StepStatus.PENDING: return "Pending";
        case StepStatus.PENDING_REVISION: return "Pending Revision";
        case StepStatus.APPROVED: return "Complete";
        case StepStatus.INACTIVE: return "Inactive";
        default: return `Status ${status}`;
    }
}

// Shared read-only template display used in both main dialog and default preview
function ReadOnlyTemplateFields({ name, subject, body }: { name: string; subject: string; body: string }) {
    return (
        <>
            <div className="etv-field">
                <span className="etv-label">Name: </span>
                <span className="etv-name-value">{name}</span>
            </div>
            <div className="etv-field">
                <div className="etv-label">Subject</div>
                <div className="etv-readonly-text">{subject || "(no subject)"}</div>
            </div>
            <div>
                <div className="etv-label">Body</div>
                <pre className="etv-readonly-body">{body || "(no body)"}</pre>
            </div>
        </>
    );
}

export default function EmailTemplateViewerDialog({
    visible, onHiding, templateName, specialSettings,
    contextId, contextType, phaseTypeId, resetContext,
    onTemplateCustomized, onTemplateReset, onSettingsChanged,
}: Props) {
    const [template, setTemplate] = useState<EmailTemplateContentDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editSubject, setEditSubject] = useState("");
    const [editBody, setEditBody] = useState("");
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [coreTemplate, setCoreTemplate] = useState<EmailTemplateContentDto | null>(null);
    const [showCorePreview, setShowCorePreview] = useState(false);
    // editMode: user clicked Customize on a core template but hasn't saved yet
    const [editMode, setEditMode] = useState(false);
    const [normalizedFrom, setNormalizedFrom] = useState<string | null>(null);
    // Dynamic tokens fetched from backend based on template type
    const [tokenReference, setTokenReference] = useState<TemplateTokenInfo[]>(emptyTokens);
    const subjectTokens = tokenReference.filter(t => t.scope.includes("Subject"));

    // A non-core template is customized at this level if its name ends with _contextId (the suffix convention from customizeEmailTemplate).
    const isCustomizedAtThisLevel = template != null && !template.isCore
        && contextId != null && template.name.endsWith(`_${contextId}`);
    const isEditable = isCustomizedAtThisLevel || editMode;
    const canCustomize = template != null && !isCustomizedAtThisLevel && contextId != null && !editMode;
    const canEditSettings = contextId != null;

    useEffect(() => {
        if (!visible || !templateName) {
            setTemplate(null);
            setError(null);
            setCoreTemplate(null);
            setShowCorePreview(false);
            setShowResetConfirm(false);
            setEditMode(false);
            setNormalizedFrom(null);
            setTokenReference(emptyTokens);
            return;
        }

        const load = async () => {
            setLoading(true);
            setError(null);
            const result = await getEmailTemplate(templateName);
            if (result?.data) {
                setTemplate(result.data);
                setEditSubject(result.data.subject);
                setEditBody(result.data.body);
                // Backend normalized a dangling reference — update the tab and notify
                if (result.data.name !== templateName) {
                    setNormalizedFrom(templateName);
                    onTemplateReset?.(result.data.name);
                }
                // Fetch context-aware tokens based on template type
                if (result.data.templateType) {
                    const tokenResult = await getEmailTemplateTokens(result.data.templateType);
                    if (tokenResult?.data) setTokenReference(tokenResult.data);
                }
            } else {
                setError(result?.error?.message ?? "Template not found");
            }
            setLoading(false);
        };
        load();
    }, [visible, templateName]);

    const handleCustomize = () => {
        if (!template || !contextId) return;
        setEditMode(true);
    };

    const handleSave = async () => {
        if (!template || !contextId) return;
        // In editMode (first customize), the current template is the base to copy from.
        // For an existing custom template, derive the base from the parent default or suffix.
        const coreName = editMode
            ? template.name
            : template.name.replace(`_${contextId}`, "");
        setSaving(true);
        const result = await customizeEmailTemplate({
            coreTemplateName: coreName,
            contextId,
            subject: editSubject,
            body: editBody,
        });
        if (result?.data) {
            setTemplate(result.data);
            setEditSubject(result.data.subject);
            setEditBody(result.data.body);
            if (editMode) {
                setEditMode(false);
                onTemplateCustomized?.(result.data.name);
            }
        }
        setSaving(false);
    };

    const handleResetConfirmed = async () => {
        if (!template) return;
        setSaving(true);
        setShowResetConfirm(false);
        const dto: ResetEmailTemplateDto = {
            templateName: template.name,
            defaultPhaseId: resetContext?.defaultPhaseId ?? null,
            defaultStepId: resetContext?.defaultStepId ?? null,
            trigger: resetContext?.trigger ?? null,
            fromStepStatus: resetContext?.fromStepStatus ?? null,
            toStepStatus: resetContext?.toStepStatus ?? null,
        };
        const resetResult = await resetEmailTemplate(dto);
        if (resetResult?.data?.revertToTemplateName) {
            const revertTo = resetResult.data.revertToTemplateName;
            const revertResult = await getEmailTemplate(revertTo);
            if (revertResult?.data) {
                setTemplate(revertResult.data);
                setEditSubject(revertResult.data.subject);
                setEditBody(revertResult.data.body);
            }
            setCoreTemplate(null);
            setShowCorePreview(false);
            onTemplateReset?.(revertTo);
        }
        setSaving(false);
    };

    const [loadingCore, setLoadingCore] = useState(false);

    const handleViewDefault = async () => {
        if (!template || !contextId) return;
        setShowCorePreview(true);
        if (!coreTemplate) {
            setLoadingCore(true);
            const revertTo = template.name.replace(`_${contextId}`, "");
            const result = await getEmailTemplate(revertTo);
            if (result?.data) setCoreTemplate(result.data);
            setLoadingCore(false);
        }
    };

    // Prevent wheel events from bubbling to popup scroll container
    const handleSidebarWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const atTop = el.scrollTop === 0 && e.deltaY < 0;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0;
        if (!atTop && !atBottom) {
            e.stopPropagation();
        }
    }, []);

    // Normalize line endings for comparison — API returns \r\n but textarea may normalize to \n
    const normalize = (s: string) => s.replace(/\r\n/g, "\n");
    const isDirty = isEditable && template != null && (normalize(editSubject) !== normalize(template.subject) || normalize(editBody) !== normalize(template.body));

    return (
        <>
            <Popup
                visible={visible}
                onHiding={onHiding}
                showCloseButton
                hideOnOutsideClick
                dragEnabled
                title="Email Template"
                width={900}
                height="auto"
                maxHeight="80vh"
            >
                {loading && <div className="etv-loading">Loading template...</div>}
                {error && <div className="etv-error">{error}</div>}
                {template && (
                    <div className="etv-content">
                        {normalizedFrom && (
                            <div className="etv-notice">
                                <span>{normalizedFrom} no longer exists. Template reset to the default.</span>
                                <button className="etv-notice-close" onClick={() => setNormalizedFrom(null)}>&times;</button>
                            </div>
                        )}
                        <div className="etv-layout">
                            <div className="etv-main">
                                {!isEditable ? (
                                    <ReadOnlyTemplateFields name={template.name} subject={template.subject} body={template.body} />
                                ) : (
                                    <>
                                        <div className="etv-field">
                                            <span className="etv-label">Name: </span>
                                            <span className="etv-name-value">{template.name}</span>
                                        </div>
                                        <div className="etv-field">
                                            <div className="etv-label">Subject</div>
                                            <TokenEditor
                                                value={editSubject}
                                                onChange={setEditSubject}
                                                tokens={subjectTokens}
                                                height="38px"
                                            />
                                        </div>
                                        <div>
                                            <div className="etv-label">Body</div>
                                            <TokenEditor
                                                value={editBody}
                                                onChange={setEditBody}
                                                tokens={tokenReference}
                                                height="300px"
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="etv-actions">
                                    {canCustomize && (
                                        <Button
                                            text="Customize"
                                            type="default"
                                            stylingMode="contained"
                                            onClick={handleCustomize}
                                            disabled={saving}
                                        />
                                    )}
                                    {isEditable && (
                                        <>
                                            <Button
                                                text={isDirty ? "Save *" : "Save"}
                                                type="default"
                                                stylingMode="contained"
                                                onClick={handleSave}
                                                disabled={saving || !isDirty}
                                            />
                                            {isCustomizedAtThisLevel && (
                                                <>
                                                    <Button
                                                        text="View Default"
                                                        type="normal"
                                                        stylingMode="outlined"
                                                        onClick={handleViewDefault}
                                                        disabled={saving}
                                                    />
                                                    <Button
                                                        text="Reset to Default"
                                                        type="danger"
                                                        stylingMode="outlined"
                                                        onClick={() => setShowResetConfirm(true)}
                                                        disabled={saving}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                    {isEditable && (
                                        <span className="etv-status-label">
                                            {editMode ? "Customizing (unsaved)" : "Custom template (editable)"}
                                        </span>
                                    )}
                                    {template.isCore && !contextId && (
                                        <span className="etv-status-label">
                                            Core template (read-only)
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="etv-sidebar" onWheel={handleSidebarWheel}>
                                <div className="etv-label" style={{ marginBottom: "8px" }}>Token Reference</div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Token</th>
                                            <th>Scope</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tokenReference.map(t => (
                                            <tr key={t.token} title={t.description}>
                                                <td>{t.token}</td>
                                                <td>{t.scope}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {specialSettings && (
                            <div className="etv-settings">
                                <div style={{ display: "flex", gap: "32px", marginBottom: "8px" }}>
                                    <div className="etv-label" style={{ width: "33%", flexShrink: 0 }}>Notification Settings</div>
                                    {phaseTypeId != null && <div className="etv-label">Attached Files</div>}
                                </div>
                                <div className="etv-settings-layout">
                                    <div className="etv-settings-checkboxes">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={specialSettings.attachStepFiles}
                                                disabled={!canEditSettings}
                                                onChange={(e) => onSettingsChanged?.({ attachStepFiles: e.target.checked })}
                                            />
                                            {contextType === "subtask" ? "Attach request files" : "Attach subtask request files"}
                                            {specialSettings.attachStepFilesForStatuses && specialSettings.attachStepFilesForStatuses.length > 0 && (
                                                <span className="etv-settings-status-filter">
                                                    (only from: {specialSettings.attachStepFilesForStatuses.map(s => getStepStatusLabel(s)).join(", ")})
                                                </span>
                                            )}
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={specialSettings.attachGeneratedReport}
                                                disabled={!canEditSettings}
                                                onChange={(e) => onSettingsChanged?.({ attachGeneratedReport: e.target.checked })}
                                            />
                                            Attach generated report
                                        </label>
                                        {/* Phase approval is all-or-nothing, so section filtering only applies at the subtask level. */}
                                        {contextType !== "task" && (
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={specialSettings.onlyApprovedSections}
                                                    disabled={!canEditSettings || !specialSettings.attachGeneratedReport}
                                                    onChange={(e) => onSettingsChanged?.({ onlyApprovedSections: e.target.checked })}
                                                />
                                                Only include approved/completed sections
                                            </label>
                                        )}
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={specialSettings.showPendingApprovalWatermark}
                                                disabled={!canEditSettings || !specialSettings.attachGeneratedReport}
                                                onChange={(e) => onSettingsChanged?.({ showPendingApprovalWatermark: e.target.checked })}
                                            />
                                            Show "Pending Approval" watermark
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={specialSettings.showCompletionStamp}
                                                disabled={!canEditSettings || !specialSettings.attachGeneratedReport}
                                                onChange={(e) => onSettingsChanged?.({ showCompletionStamp: e.target.checked })}
                                            />
                                            Stamp approved/completed sections
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={specialSettings.showCoverPage}
                                                disabled={!canEditSettings || !specialSettings.attachGeneratedReport}
                                                onChange={(e) => onSettingsChanged?.({ showCoverPage: e.target.checked })}
                                            />
                                            Include cover page
                                        </label>
                                    </div>

                                    {phaseTypeId != null && (
                                        <TemplateAttachments
                                            phaseTypeId={phaseTypeId}
                                            selectedFiles={specialSettings.appDataAttachments ?? []}
                                            onFilesChanged={(files) => onSettingsChanged?.({ appDataAttachments: files.length > 0 ? files : null })}
                                            canEdit={canEditSettings}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Popup>

            {/* Reset confirmation dialog */}
            <Popup
                visible={showResetConfirm}
                onHiding={() => setShowResetConfirm(false)}
                showCloseButton
                hideOnOutsideClick
                dragEnabled={false}
                title="Reset to Default"
                width={450}
                height="auto"
                wrapperAttr={{ style: "z-index: 1600" }}
            >
                <div className="etv-confirm-content">
                    <p>
                        This will permanently delete the customized email template and revert to the default.
                        This action cannot be undone.
                    </p>
                    <p>Are you sure you want to continue?</p>
                    <div className="etv-confirm-actions">
                        <Button
                            text="Cancel"
                            type="normal"
                            stylingMode="outlined"
                            onClick={() => setShowResetConfirm(false)}
                        />
                        <Button
                            text="Reset to Default"
                            type="danger"
                            stylingMode="contained"
                            onClick={handleResetConfirmed}
                            disabled={saving}
                        />
                    </div>
                </div>
            </Popup>

            {/* Default template preview dialog */}
            <Popup
                visible={showCorePreview}
                onHiding={() => setShowCorePreview(false)}
                showCloseButton
                hideOnOutsideClick
                dragEnabled
                title="Default Template"
                width={700}
                height="auto"
                maxHeight="70vh"
                wrapperAttr={{ style: "z-index: 1600" }}
            >
                {loadingCore && <div className="etv-loading">Loading default template...</div>}
                {coreTemplate && !loadingCore && (
                    <div className="etv-content">
                        <ReadOnlyTemplateFields name={coreTemplate.name} subject={coreTemplate.subject} body={coreTemplate.body} />
                    </div>
                )}
            </Popup>

        </>
    );
}
